import React from 'react'
import './styles/index.scss'
import { BUTTON_VARIANT } from 'utils/constants'
import ButtonLoading from './components/ButtonLoading'

/**
 * Reusable Button component for various UI actions.
 *
 * @param {Object} props - Button component props.
 * @param {boolean} [props.disabled=false] - If true, the button is disabled.
 * @param {string} [props.variant=BUTTON_VARIANT.PRIMARY] - Visual style of the button. Should be one of BUTTON_VARIANT values.
 * @param {Function} [props.onClick=() => {}] - Click event handler.
 * @param {string} [props.className=''] - Additional CSS classes for the button.
 * @param {Object} [props.style={}] - Inline styles for the button.
 * @param {React.ReactNode} [props.children=null] - Content to display inside the button. If not provided, `label` is used.
 * @param {string} [props.label=''] - Text label for the button (used if `children` is not provided).
 * @param {string} [props.size=''] - Size modifier for the button (e.g., 'small', 'large').
 * @param {string} [props.type=''] - Button type attribute (e.g., 'button', 'submit', 'reset').
 * @param {string} [props.id=''] - HTML id attribute for the button.
 * @param {boolean} [props.hoverEffect=true] - If true, applies a hover effect style.
 *
 * @returns {JSX.Element} The rendered button element.
 *
 * @example
 * <Button
 *   variant={BUTTON_VARIANT.SECONDARY}
 *   onClick={handleClick}
 *   label='Click Me'
 *   size='large'
 * />
 */
const Button = (props) => {
  const {
    disabled = false,
    loading = false,
    variant = BUTTON_VARIANT.PRIMARY,
    onClick = () => {},
    className = '',
    style = {},
    children = null,
    label = '',
    size = '',
    type = '',
    id = undefined,
    hoverEffect = true
  } = props

  return (
    <button
      id={id}
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      style={style}
      className={`btn__${variant} ${variant === BUTTON_VARIANT.CONTENT_ONLY ? '' : 'button_common'} ${hoverEffect ? 'hover_effect' : ''} ${loading ? 'button_loading' : ''} ${size} ${className || ''}`}
    >
      <span className='button__text'>{children || label}</span>
      {loading && <ButtonLoading />}
    </button>
  )
}

export const PrimaryButton = Button
export const SecondaryButton = Button

export default Button
